// Decide whether a SQL statement can be put into Live mode and, if so,
// what table to subscribe to.
//
// Live mode strategy: open `WATCH <table>` and re-run the original SELECT
// every time the server pushes a `watch_notify`. The query result then stays
// in sync without us having to interpret aggregates / joins client-side.
//
// We accept any single-table SELECT (with or without WHERE / ORDER BY /
// LIMIT). We reject:
//   - non-SELECT statements
//   - JOINs (would need to subscribe to multiple tables)
//   - GROUP BY / HAVING (aggregates may surprise on partial updates)
//   - comma-FROM (multiple tables)
//   - subquery in FROM (`FROM (SELECT ...)`) — table name isn't an identifier
//   - EXPLAIN (re-running EXPLAIN on every change isn't useful)

const STOP_RE = /\b(WHERE|ORDER\s+BY|LIMIT|GROUP\s+BY|HAVING|JOIN|UNION)\b/i;

export function parseWatchable(rawSql) {
  if (!rawSql) return { ok: false, reason: 'Empty query' };
  const sql = rawSql.replace(/;\s*$/, '').trim();
  if (/^EXPLAIN\b/i.test(sql))   return { ok: false, reason: 'Live mode runs the SELECT, not EXPLAIN' };
  if (!/^SELECT\b/i.test(sql))    return { ok: false, reason: 'Live mode is for SELECT queries only' };
  if (/\bJOIN\b/i.test(sql))      return { ok: false, reason: 'JOINs aren\'t supported in Live mode yet' };
  if (/\bGROUP\s+BY\b/i.test(sql))return { ok: false, reason: 'GROUP BY isn\'t supported in Live mode yet' };
  if (/\bHAVING\b/i.test(sql))    return { ok: false, reason: 'HAVING isn\'t supported in Live mode yet' };
  if (/\bUNION\b/i.test(sql))     return { ok: false, reason: 'UNION isn\'t supported in Live mode yet' };

  // Capture the slice between FROM and the next clause keyword. If the slice
  // contains a comma, we have a multi-table FROM. Otherwise, the slice should
  // be a bare identifier.
  const m = sql.match(/\bFROM\b([\s\S]*?)(?:\b(?:WHERE|ORDER\s+BY|LIMIT|GROUP\s+BY|HAVING)\b|$)/i);
  if (!m) return { ok: false, reason: 'Couldn\'t locate FROM clause' };
  let fromTxt = m[1].trim();
  if (!fromTxt) return { ok: false, reason: 'Empty FROM clause' };
  if (fromTxt.startsWith('(')) return { ok: false, reason: 'Subqueries in FROM aren\'t supported in Live mode' };
  if (fromTxt.includes(',')) return { ok: false, reason: 'Multiple tables in FROM aren\'t supported in Live mode' };

  // Strip an optional alias: "users u" or "users AS u".
  const tokens = fromTxt.split(/\s+/);
  const table = tokens[0];
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(table)) {
    return { ok: false, reason: `Couldn\'t identify the table name (got "${table}")` };
  }
  // Reject if any of the remaining tokens look like a clause keyword that we
  // missed — defensive paranoia in case the grammar adds something we don\'t
  // know about.
  if (tokens.slice(1).some(t => STOP_RE.test(t))) {
    return { ok: false, reason: 'Unsupported clause after FROM' };
  }
  return { ok: true, table };
}
